import { useState } from 'react';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CAREER_ENTRY_DETAILS_MAX_LENGTH,
  CAREER_ORGANIZATION_SEARCH_MIN_TERM_LENGTH,
} from '../../../../appConfig';
import {
  CREATE_CAREER_ENTRY,
  UPDATE_CAREER_ENTRY,
} from '../../../../graphql/mutations/careerHistory';
import {
  GET_MY_CAREER_ENTRIES,
  SEARCH_CAREER_ORGANIZATIONS,
} from '../../../../graphql/queries/careerHistory';
import { GET_USER_ACCOUNT } from '../../../../graphql/queries/userAccount';
import {
  CareerRoleEnum,
  type CreateCareerEntryMutation,
  type CreateCareerEntryMutationVariables,
  type GetMyCareerEntriesQuery,
  type SearchCareerOrganizationsQuery,
  type SearchCareerOrganizationsQueryVariables,
  type UpdateCareerEntryMutation,
  type UpdateCareerEntryMutationVariables,
} from '../../../../types/graphql-generated';
import { CAREER_ROLE_LABELS, CAREER_ROLE_OPTIONS, formatCareerStatus } from '../careerRoles';
import { CareerDatePicker } from './CareerDatePicker';

// `id` is null for an organization typed by the user that is not in the
// catalogue: the backend reuses one with that name or creates it unregistered.
interface SelectedOrganization {
  id: string | null;
  name: string;
}

const normalizeName = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

export type CareerEntry = GetMyCareerEntriesQuery['myCareerEntries'][number];

interface CareerEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  // When present, the modal edits this entry instead of creating a new one.
  // The parent remounts the modal (via `key`) when it changes, so the form
  // state is initialized from it.
  entry?: CareerEntry | null;
}

const FormSectionHeader = ({ children }: { children: string }) => (
  <h2 className="m-0 pt-2 text-center font-heading text-xl font-bold text-gray-900">{children}</h2>
);

export const CareerEntryModal = ({ isOpen, onClose, entry = null }: CareerEntryModalProps) => {
  const isEditing = entry !== null;
  const [role, setRole] = useState<CareerRoleEnum | null>(entry?.role ?? null);
  const [startDate, setStartDate] = useState(entry?.startDate ?? '');
  const [endDate, setEndDate] = useState(entry?.endDate ?? '');
  const [details, setDetails] = useState(entry?.details ?? '');
  const [isCurrent, setIsCurrent] = useState(entry ? !entry.endDate : true);
  const [setAsCareerStatus, setSetAsCareerStatus] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganization, setSelectedOrganization] =
    useState<SelectedOrganization | null>(entry?.careerOrganization ?? null);
  const [errors, setErrors] = useState<string[]>([]);
  const [presentToast] = useIonToast();

  const trimmedSearchTerm = searchTerm.trim();
  const canSearch = trimmedSearchTerm.length >= CAREER_ORGANIZATION_SEARCH_MIN_TERM_LENGTH;

  const { data: organizationData, loading: searching } = useQuery<
    SearchCareerOrganizationsQuery,
    SearchCareerOrganizationsQueryVariables
  >(SEARCH_CAREER_ORGANIZATIONS, {
    variables: { term: trimmedSearchTerm, limit: 20 },
    skip: !isOpen || !canSearch || selectedOrganization !== null,
  });

  const [createCareerEntry, { loading: creating }] = useMutation<
    CreateCareerEntryMutation,
    CreateCareerEntryMutationVariables
  >(CREATE_CAREER_ENTRY, {
    awaitRefetchQueries: true,
  });

  const [updateCareerEntry, { loading: updating }] = useMutation<
    UpdateCareerEntryMutation,
    UpdateCareerEntryMutationVariables
  >(UPDATE_CAREER_ENTRY, {
    refetchQueries: [{ query: GET_MY_CAREER_ENTRIES }],
    awaitRefetchQueries: true,
  });

  const saving = creating || updating;

  const reset = () => {
    setRole(null);
    setStartDate('');
    setEndDate('');
    setDetails('');
    setIsCurrent(true);
    setSetAsCareerStatus(false);
    setSearchTerm('');
    setSelectedOrganization(null);
    setErrors([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleOrganizationSearch = (value: string) => {
    setSearchTerm(value);
    setSelectedOrganization(null);
  };

  const handleSubmit = async () => {
    const validationErrors: string[] = [];
    if (!selectedOrganization) validationErrors.push('Selecciona una entidad');
    if (!role) validationErrors.push('Selecciona un rol');
    if (!startDate) validationErrors.push('Indica la fecha de inicio');
    if (!isCurrent && !endDate) validationErrors.push('Indica la fecha de fin');
    if (!isCurrent && startDate && endDate && endDate < startDate) {
      validationErrors.push('La fecha de fin no puede ser anterior a la fecha de inicio');
    }

    if (validationErrors.length > 0 || !selectedOrganization || !role || !startDate) {
      setErrors(validationErrors);
      return;
    }

    setErrors([]);
    const registerInProfile = !isEditing && isCurrent && setAsCareerStatus;

    const fields = {
      ...(selectedOrganization.id
        ? { careerOrganizationId: selectedOrganization.id }
        : { organizationName: selectedOrganization.name }),
      role,
      startDate,
      endDate: isCurrent ? null : endDate,
      details: details.trim() || null,
    };

    try {
      const result = entry
        ? (await updateCareerEntry({ variables: { input: { id: entry.id, ...fields } } })).data
            ?.updateCareerEntry
        : (
            await createCareerEntry({
              variables: { input: { ...fields, setAsCareerStatus: registerInProfile } },
              refetchQueries: registerInProfile
                ? [{ query: GET_MY_CAREER_ENTRIES }, { query: GET_USER_ACCOUNT }]
                : [{ query: GET_MY_CAREER_ENTRIES }],
            })
          ).data?.createCareerEntry;

      if (result?.careerEntry && result.errors.length === 0) {
        presentToast({
          message: isEditing ? 'Experiencia actualizada' : 'Experiencia añadida al historial',
          duration: 2500,
          position: 'bottom',
          positionAnchor: 'app-footer',
          cssClass: 'toast-success',
          buttons: [{ icon: closeOutline, role: 'cancel' }],
        });
        handleClose();
      } else {
        setErrors(result?.errors ?? ['No se ha podido guardar la experiencia']);
      }
    } catch (error) {
      console.error('Error saving career entry:', error);
      setErrors(['No se ha podido guardar la experiencia. Inténtalo de nuevo más tarde.']);
    }
  };

  const organizations = organizationData?.careerOrganizations ?? [];
  const careerStatusLine =
    role && selectedOrganization
      ? formatCareerStatus(CAREER_ROLE_LABELS[role], selectedOrganization.name)
      : null;
  const canUseTypedName =
    canSearch &&
    !searching &&
    !organizations.some(
      (organization) => normalizeName(organization.name) === normalizeName(trimmedSearchTerm),
    );

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle className="ml-4">
            {isEditing ? 'Editar experiencia' : 'Añadir experiencia'}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>Cancelar</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div className="space-y-4">
          <FormSectionHeader>Soy / he sido</FormSectionHeader>

          <IonSelect
            label="Rol"
            labelPlacement="floating"
            fill="outline"
            interface="popover"
            placeholder="Selecciona un rol"
            value={role}
            onIonChange={(event) => setRole(event.detail.value as CareerRoleEnum)}
          >
            {CAREER_ROLE_OPTIONS.map((option) => (
              <IonSelectOption key={option.value} value={option.value}>
                {option.label}
              </IonSelectOption>
            ))}
          </IonSelect>

          <FormSectionHeader>Del</FormSectionHeader>

          <div>
            {selectedOrganization ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-[#e4e8f0] bg-white p-3">
                <div className="min-w-0">
                  <span className="font-semibold text-gray-900">{selectedOrganization.name}</span>
                  {selectedOrganization.id === null && (
                    <p className="m-0 text-xs text-gray-500">
                      Entidad nueva: la revisaremos para añadirla al catálogo
                    </p>
                  )}
                </div>
                <IonButton fill="clear" size="small" onClick={() => setSelectedOrganization(null)}>
                  Cambiar
                </IonButton>
              </div>
            ) : (
              <div className="relative">
                <IonSearchbar
                  className="px-0!"
                  value={searchTerm}
                  onIonInput={(event) => handleOrganizationSearch(event.detail.value ?? '')}
                  onIonClear={() => handleOrganizationSearch('')}
                  placeholder="Busca un club, federación o medio"
                  debounce={300}
                  aria-label="Buscar entidad"
                />

                {trimmedSearchTerm.length > 0 && (
                  <div className="absolute inset-x-0 top-full z-20 overflow-hidden rounded-xl border border-[#e4e8f0] bg-white shadow-lg">
                    {!canSearch && (
                      <IonText color="medium">
                        <p className="m-0 px-4 py-3 text-sm">
                          Escribe al menos {CAREER_ORGANIZATION_SEARCH_MIN_TERM_LENGTH} caracteres
                        </p>
                      </IonText>
                    )}

                    {searching && (
                      <div className="flex justify-center py-4">
                        <IonSpinner name="crescent" />
                      </div>
                    )}

                    {canSearch && !searching && organizations.length === 0 && (
                      <IonText color="medium">
                        <p className="m-0 px-4 py-3 text-sm">No se han encontrado entidades</p>
                      </IonText>
                    )}

                    {(organizations.length > 0 || canUseTypedName) && (
                      <IonList
                        lines="full"
                        className="career-organization-options max-h-52 overflow-y-auto"
                      >
                        {organizations.map((organization) => (
                          <IonItem
                            key={organization.id}
                            button
                            detail={false}
                            onClick={() => setSelectedOrganization(organization)}
                          >
                            <IonLabel className="ion-text-wrap text-sm font-semibold text-gray-900">
                              {organization.name}
                            </IonLabel>
                          </IonItem>
                        ))}
                        {canUseTypedName && (
                          <IonItem
                            button
                            detail={false}
                            onClick={() =>
                              setSelectedOrganization({ id: null, name: trimmedSearchTerm })
                            }
                          >
                            <IonLabel className="ion-text-wrap text-sm text-gray-900">
                              Usar «<span className="font-semibold">{trimmedSearchTerm}</span>»
                            </IonLabel>
                          </IonItem>
                        )}
                      </IonList>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <FormSectionHeader>Desde</FormSectionHeader>

          <CareerDatePicker
            datetimeId="career-start-date"
            label="Fecha de inicio"
            value={startDate}
            onChange={setStartDate}
          />

          <FormSectionHeader>Hasta</FormSectionHeader>

          <IonCheckbox
            checked={isCurrent}
            onIonChange={(event) => {
              const checked = event.detail.checked;
              setIsCurrent(checked);
              if (checked) setEndDate('');
              else setSetAsCareerStatus(false);
            }}
          >
            Es mi rol actual
          </IonCheckbox>

          {!isEditing && isCurrent && (
            <IonCheckbox
              className="ion-text-wrap"
              checked={setAsCareerStatus}
              onIonChange={(event) => setSetAsCareerStatus(event.detail.checked)}
            >
              <span className="whitespace-normal">
                {careerStatusLine ? (
                  <>
                    Registrar como línea de estado: <strong>{careerStatusLine}</strong>
                  </>
                ) : (
                  <>Registrar como línea de estado (por ejemplo "Entrenador del Avilés Stadium")</>
                )}
              </span>
            </IonCheckbox>
          )}

          {!isCurrent && (
            <CareerDatePicker
              datetimeId="career-end-date"
              label="Fecha de fin"
              value={endDate}
              onChange={setEndDate}
            />
          )}

          <IonInput
            label="Más detalles (opcional)"
            labelPlacement="stacked"
            fill="outline"
            placeholder="Ejemplo: Liga Nacional"
            maxlength={CAREER_ENTRY_DETAILS_MAX_LENGTH}
            counter
            value={details}
            onIonInput={(event) => setDetails(event.detail.value?.toString() ?? '')}
            aria-label="Más detalles (opcional)"
          />

          {errors.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-3">
              {errors.map((error) => (
                <IonText color="danger" key={error}>
                  <p className="m-0 text-sm">{error}</p>
                </IonText>
              ))}
            </div>
          )}

          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={saving || !selectedOrganization || !role || !startDate || (!isCurrent && !endDate)}
          >
            {saving ? <IonSpinner name="crescent" /> : 'Guardar experiencia'}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};
